import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GenericCroutonProps, ShowCroutonArgs } from "@swiggy-private/cart-types";

import { CROUTON_CLOSE_DELAY, OVERLAY_DELAYS } from "@checkout-core/constants/overlay";
import { isEmpty } from "@swiggy-private/js-utils";

export const useRegisterCrouton = (
    setEnabledCroutonId: React.Dispatch<React.SetStateAction<{ enabledCroutonId: string }>>,
) => {
    const [finalPriorityQueue, setFinalPriorityQueue] = useState<ShowCroutonArgs>();

    const croutonContent = useRef<GenericCroutonProps | null>(null);
    const listenersRef = useRef<Set<() => void>>(new Set());

    const setCroutonContent = useCallback((content: GenericCroutonProps | null) => {
        croutonContent.current = content;
        listenersRef.current.forEach((listener) => listener());
    }, []);

    const [shouldRenderCrouton, setShouldRenderCrouton] = useState(false);

    const croutonQueue = useRef<ShowCroutonArgs[]>([]); // Queue for croutons
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timer for managing delay

    const subscribeToCroutonContent = useCallback((listener: () => void) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);
    const getCroutonContentSnapshot = useCallback(() => croutonContent.current, []);

    const registerCrouton = useCallback(
        (args: ShowCroutonArgs) => {
            const { forceRefreshContent = false } = args;
            if ((shouldRenderCrouton || !isEmpty(croutonContent?.current)) && !forceRefreshContent) return;

            // Clear any existing timer
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            // Add crouton to queue if not already present
            const idx = croutonQueue.current.findIndex((crouton) => crouton.id === args.id);
            if (idx === -1) {
                croutonQueue.current.push(args);
            } else if (forceRefreshContent) {
                croutonQueue.current[idx] = args; // replace with updated content
            }

            // Start timer to show highest priority crouton after delay
            timerRef.current = setTimeout(() => {
                if (isEmpty(croutonQueue.current)) {
                    return;
                }
                const highestPriorityCrouton = croutonQueue.current.reduce((prev, curr) =>
                    prev.priority < curr.priority ? prev : curr,
                );

                setCroutonContent(highestPriorityCrouton?.content);
                setFinalPriorityQueue(highestPriorityCrouton);
                setShouldRenderCrouton(true);
                croutonQueue.current = []; // Reset queue after displaying
            }, OVERLAY_DELAYS.CROUTON);

            // Cleanup timer on unmount
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    croutonQueue.current = [];
                }
            };
        },
        [shouldRenderCrouton, setCroutonContent],
    );

    const onCloseCrouton = useCallback(() => {
        const closeTimer = setTimeout(() => {
            setCroutonContent(null);
            setShouldRenderCrouton(false);
            croutonQueue.current = [];
        }, CROUTON_CLOSE_DELAY);

        return () => {
            clearTimeout(closeTimer);
            croutonQueue.current = [];
        };
    }, []);

    // This is used to close the crouton immediately without any delay
    const onCloseCroutonImmediate = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setCroutonContent(null);
        setShouldRenderCrouton(false);
        croutonQueue.current = [];
    }, [setCroutonContent]);

    const croutonProps = useMemo(
        () => ({
            registerCrouton,
            shouldRenderCrouton,
            onCloseCrouton,
            croutonContent: croutonContent.current,
            subscribeToCroutonContent,
            getCroutonContentSnapshot,
            enabledCroutonId: finalPriorityQueue?.id || "",
            onCloseCroutonImmediate,
        }),
        [
            registerCrouton,
            shouldRenderCrouton,
            onCloseCrouton,
            croutonContent,
            subscribeToCroutonContent,
            getCroutonContentSnapshot,
            finalPriorityQueue?.id,
            onCloseCroutonImmediate,
        ],
    );

    useEffect(() => {
        setEnabledCroutonId({
            enabledCroutonId: finalPriorityQueue?.id || "",
        });
    }, [finalPriorityQueue, setEnabledCroutonId]);

    return croutonProps;
};
